/**
 * <s-signup-popup> — full-screen welcome offer.
 * Opens once after `data-delay` seconds unless the visitor dismissed it in the last 14 days
 * or already signed up. After the signup form posts, Shopify reloads the page with
 * `#signup-popup-form`; the popup reopens immediately to show the discount code.
 * Once dismissed, a small "Get 15% off" teaser stays in the bottom-left corner on mobile
 * (until the visitor hides it or signs up); tapping it reopens the popup.
 */
const KEY = 'liftid-signup-popup';
const TEASER_KEY = 'liftid-signup-teaser-hidden';
const SNOOZE_DAYS = 14;

class SSignupPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.teaser = this.querySelector('[data-popup-teaser]');
    if (!this.dialog) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-close]')) this.close();
      if (event.target.closest('[data-popup-reopen]')) this.open(true);
      if (event.target.closest('[data-popup-teaser-close]')) this.hideTeaser();
      const copy = event.target.closest('[data-popup-copy]');
      if (copy) this.copy(copy);
    });
    this.dialog.addEventListener('cancel', () => this.remember('dismissed'));
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.updateTeaser();
    });

    const justPosted = location.hash === '#signup-popup-form';
    if (justPosted) {
      if (this.dialog.querySelector('[data-popup-copy]')) this.remember('subscribed');
      this.open(true);
      return;
    }

    this.updateTeaser();
    if (!this.shouldShow()) return;
    const delay = Math.max(0, Number(this.dataset.delay) || 5) * 1000;
    this.timer = setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  saved() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null');
    } catch {
      return null;
    }
  }

  shouldShow() {
    const saved = this.saved();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    return Date.now() - saved.at > SNOOZE_DAYS * 864e5;
  }

  remember(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {}
  }

  updateTeaser() {
    if (!this.teaser) return;
    let teaserHidden = false;
    try {
      teaserHidden = localStorage.getItem(TEASER_KEY) === '1';
    } catch {}
    const saved = this.saved();
    const show = saved?.state === 'dismissed' && !teaserHidden && !this.dialog.open;
    this.teaser.hidden = !show;
  }

  hideTeaser() {
    try {
      localStorage.setItem(TEASER_KEY, '1');
    } catch {}
    this.updateTeaser();
  }

  open(force = false) {
    if (this.dialog.open) return;
    if (!force && document.querySelector('dialog[open]')) return;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    if (this.teaser) this.teaser.hidden = true;
  }

  close() {
    if (!this.dialog.querySelector('[data-popup-copy]')) this.remember('dismissed');
    this.dialog.close();
    if (location.hash === '#signup-popup-form') history.replaceState(null, '', location.pathname + location.search);
  }

  async copy(button) {
    try {
      await navigator.clipboard.writeText(button.dataset.popupCopy);
      const label = button.querySelector('[data-popup-copy-label]');
      const original = label.textContent;
      label.textContent = button.dataset.copiedLabel;
      setTimeout(() => (label.textContent = original), 1800);
    } catch {}
  }
}

if (!customElements.get('s-signup-popup')) customElements.define('s-signup-popup', SSignupPopup);
